<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';
import { Plus } from 'lucide-vue-next';

// shadcn-vue 컴포넌트 임포트
import { Button } from '@/components/shadcnui/button';
import { Input } from '@/components/shadcnui/input';
import { Label } from '@/components/shadcnui/label';
import { Skeleton } from '@/components/shadcnui/skeleton';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/shadcnui/tooltip';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/shadcnui/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/shadcnui/dialog';
import { Pagination, PaginationContent, PaginationEllipsis, PaginationItem, PaginationNext, PaginationPrevious } from '@/components/shadcnui/pagination';
// composable 및 store 임포트
import { useRSStore } from '@/store/useRSStore';
import { useUserStore } from '@/store/useUserStore';
import { useFwtype } from '@/composable/useFwtype';
// 타입 임포트
import type { fwtype } from '@/types/types';

// 스토어 및 composable 초기화
const RSStore = useRSStore();
const UserStore = useUserStore();
const { onReadFwtypeList, onAddFwtype, onUpdateFwtype, onDeleteFwtype, onPaginationFwtype } = useFwtype();

// 상태 관리
const isDialogOpen = ref(false);
const isUpdateMode = ref(false);
const currentPage = ref(1);
const totalPage = ref(1);
const isLoading = ref(true);
const formData = ref<fwtype>({ id: 0, name: "" });
const isFormInvalid = computed(() => {
  return !formData.value.name?.trim();
});

// 폼 데이터 초기화 함수
const formDataReset = () => {
  formData.value = { id: 0, name: ""};
};

// 버튼 핸들러
const openAddDialog = () => {

  isUpdateMode.value = false;
  formDataReset();
  isDialogOpen.value = true;
};
const openUpdateDialog = (type: fwtype) => {
  if (!UserStore.fwtypeEdit && !UserStore.fwtypeDelete) {
    return;
  }
  isUpdateMode.value = true;
  formData.value.id = type.id;
  formData.value.name = type.name;
  isDialogOpen.value = true;
};
const cancelDialog = () => {
  isDialogOpen.value = false;
  formDataReset();
};

// ADD/UPDATE/DELETE API 호출 핸들러 (API 호출 후 데이터 갱신)
const handleAdd = async () => {
  currentPage.value = 1; // 페이지네이션을 첫 페이지로 리셋
  UserStore.fwtypePage.page = 1;
  isLoading.value = true;
  await onAddFwtype(formData.value.name);
  await onReadFwtypeList(UserStore.fwtypePage).finally(() => {
    isLoading.value = false;
  });
  isDialogOpen.value = false;
};
const handleUpdate = async () => {
  const updatedData: fwtype = { ...formData.value };
  isLoading.value = true;
  await onUpdateFwtype(updatedData);
  await onReadFwtypeList(UserStore.fwtypePage).finally(() => {
    isLoading.value = false;
  });
  isDialogOpen.value = false;
};
const handleDelete = async () => {
  isLoading.value = true;
  await onDeleteFwtype(formData.value.id);
  await onReadFwtypeList(UserStore.fwtypePage).finally(() => {
    isLoading.value = false;
  });
  isDialogOpen.value = false;
};

// 페이지네이션 핸들러
const paginationHandler = async (page: number) => {
  if (page < 1) {
    page = 1;
  } else if (page > totalPage.value) {
    page = totalPage.value;
  }
  currentPage.value = page;
  UserStore.fwtypePage.page = currentPage.value;
  isLoading.value = true;
  await onReadFwtypeList(UserStore.fwtypePage).finally(() => {
    isLoading.value = false;
  });
};

// 컴포넌트 마운트 시 데이터 로드
onMounted(async () => {
  totalPage.value = await onPaginationFwtype(UserStore.itemPerPage) ?? 1;
  if (UserStore.fwtypePage.page) {
    currentPage.value = UserStore.fwtypePage.page;
  }
  await onReadFwtypeList(UserStore.fwtypePage).finally(() => {
    isLoading.value = false;
  });
});
</script>

<template>
  <div class="p-8">
    <!-- 헤더 -->
    <div class="flex items-center justify-between mb-4">
      <h1 class="text-3xl font-bold text-foreground">펌웨어 타입</h1>
      <Button @click="openAddDialog" :disabled="!UserStore.fwtypeAdd">
        <Plus class="mr-2 h-4 w-4" /> Add
      </Button>
    </div>
    <!-- 데이터 테이블 -->
    <div class="border rounded-lg bg-card overflow-hidden">
      <Table>
        <TableHeader>
          <TableRow class="bg-muted/50 hover:bg-muted/50">
            <TableHead class="font-semibold w-[1000px] px-5">ID</TableHead>
            <TableHead class="font-semibold w-[1000px]">Name</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          <!-- 로딩 상태 -->
          <template v-if="isLoading">
            <TableRow v-for="i in UserStore.itemPerPage" :key="i">
              <TableCell colspan="2" class="px-3">
                <Skeleton class="h-5" />
              </TableCell>
            </TableRow>
          </template>
          <template v-else>
          <TableRow v-for="type in RSStore.fwtypeList" :key="type.id" class="cursor-pointer hover:bg-accent/50"
            @click="openUpdateDialog(type)">
            <TableCell class="px-5 ">{{ type.id }}</TableCell>
            <TableCell class=" text-foreground ">{{ type.name }}</TableCell>
          </TableRow>
          <TableRow v-for="i in (UserStore.itemPerPage - RSStore.fwtypeList.length)" :key="'empty-' + i">
            <TableCell colspan="2">&nbsp;</TableCell>
          </TableRow>
          </template>
        </TableBody>
      </Table>
    </div>
    <!-- 페이지네이션 -->
    <div class="mt-4 flex justify-center">
      <Pagination v-model:page="currentPage" :total="totalPage * UserStore.itemPerPage" :items-per-page="UserStore.itemPerPage"
        :sibling-count="UserStore.siblingCount" show-edges>
        <PaginationContent v-slot="{ items }" class="flex items-center gap-1">
          <PaginationPrevious @click="paginationHandler(currentPage - RSStore.nextunit)" />
          <template v-for="(item, index) in items">
            <PaginationItem v-if="item.type === 'page'" :key="index" :value="item.value" as-child>
              <Button class="w-10 h-10 p-0" :variant="item.value === currentPage ? 'default' : 'outline'"
                @click="paginationHandler(item.value)">
                {{ item.value }}
              </Button>
            </PaginationItem>
            <PaginationEllipsis v-else :key="item.type" :index="index" />
          </template>
          <PaginationNext @click="paginationHandler(currentPage + RSStore.nextunit)" />
        </PaginationContent>
      </Pagination>
    </div>
    <!-- 추가/수정 다이얼로그 -->
    <Dialog v-model:open="isDialogOpen">
      <DialogContent class="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>
            {{ isUpdateMode ? "펌웨어 타입 수정" : "펌웨어 타입 추가" }}
          </DialogTitle>
          <DialogDescription>
            {{ isUpdateMode ? "펌웨어 타입 정보를 수정하세요" : "새로운 펌웨어 타입을 입력하세요" }}
          </DialogDescription>
        </DialogHeader>
        <div class="grid gap-4 py-4">
          <div class="grid gap-2">
            <Label for="name">Name</Label>
            <Input id="name" v-model="formData.name" placeholder="펌웨어 타입 이름 입력" maxlength="16" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" @click="cancelDialog">Cancel</Button>
          <Button v-if="isUpdateMode" variant="destructive" @click="handleDelete()" :disabled="!UserStore.fwtypeDelete">
            Delete
          </Button>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger as-child>
                <div class="inline-block cursor-not-allowed">
                  <Button @click="isUpdateMode ? handleUpdate() : handleAdd()"
                    :disabled="isFormInvalid || (!UserStore.fwtypeEdit && isUpdateMode)" :class="{ 'pointer-events-none': isFormInvalid }">
                    {{ isUpdateMode ? "Update" : "Add" }}
                  </Button>
                </div>
              </TooltipTrigger>
              <TooltipContent>
                <p v-if="isFormInvalid">name을 입력해주세요.</p>
                <p v-else>펌웨어 타입을 저장합니다.</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  </div>
</template>